/**
 * i18n public entry point.
 *
 * Runtime choice: a small, typed, zero-dependency runtime instead of
 * react-i18next + i18next. Rationale —
 *   1. TypeScript strict, typed keys are a first-class requirement here: the
 *      `TranslationKey` union is derived from the French source so every `t(...)`
 *      call is checked and every locale is held to structural parity at compile
 *      time. That guarantee is native here; with react-i18next it needs extra
 *      `resources` type augmentation and is still weaker for deep dotted keys.
 *   2. Zero new dependencies — this SPA deliberately hand-rolls its shell
 *      (service worker, command palette, icons, confirm dialogs) and already has
 *      zustand, a perfect fit for a reactive locale store. i18next's backends /
 *      language-detector / ICU plural machinery are unused weight for a
 *      French-first CSR app whose foundation needs catalogs + fallback + RTL.
 *   3. The whole runtime is ~4 small modules and mirrors patterns already in the
 *      repo (see stores/ui-store.ts), so it is easy to read and extend.
 *
 * Public API:
 *   • initI18n()            — apply the persisted locale to <html> before render
 *   • <I18nProvider/>       — root provider keeping <html lang/dir> in sync
 *   • useT() / useTranslation() — the hook: { t, locale, setLocale }
 *   • i18n.changeLanguage() — imperative language switch (react-i18next-style)
 *   • config exports        — SUPPORTED_LOCALES, LOCALE_META, RTL_LANGUAGES, …
 */

import { applyLocaleToDocument, useLocaleStore } from './store';

export { I18nProvider } from './I18nProvider';
export { useT, useTranslation, translate } from './useT';
export type { TranslationKey, TVars, UseTranslation } from './useT';
export { useLocaleStore, i18n, applyLocaleToDocument } from './store';
export { catalogs } from './locales';
export type { TranslationKeys } from './locales';
export {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  RTL_LANGUAGES,
  LOCALE_META,
  LOCALE_STORAGE_KEY,
  isLocale,
  dirFor,
  type Locale,
} from './config';

/**
 * Initialise i18n. Idempotent — reads the already-hydrated store locale and
 * applies `<html lang/dir>` so the first paint is correct. Call once in the app
 * bootstrap, before the router renders.
 */
export function initI18n(): void {
  applyLocaleToDocument(useLocaleStore.getState().locale);
}
